import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Package, Heart, CheckCircle, ThumbsUp, HelpCircle, Camera, X, Check, PawPrint, BookOpen, Sparkles, Tag } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';

const WaveDivider = ({ color = '#9CAF88', flip = false }) => (
  <div className={`w-full overflow-hidden leading-[0] ${flip ? 'rotate-180' : ''}`}>
    <svg viewBox="0 0 1200 20" preserveAspectRatio="none" className="w-full h-5">
      <path 
        d="M0 8.46C8 3.85 16 3.85 24 8.46C32 13.08 40 13.08 48 8.46C56 3.85 64 3.85 72 8.46C80 13.08 88 13.08 96 8.46C104 3.85 112 3.85 120 8.46C128 13.08 136 13.08 144 8.46C152 3.85 160 3.85 168 8.46C176 13.08 184 13.08 192 8.46C200 3.85 208 3.85 216 8.46C224 13.08 232 13.08 240 8.46C248 3.85 256 3.85 264 8.46C272 13.08 280 13.08 288 8.46C296 3.85 304 3.85 312 8.46C320 13.08 328 13.08 336 8.46C344 3.85 352 3.85 360 8.46C368 13.08 376 13.08 384 8.46C392 3.85 400 3.85 408 8.46C416 13.08 424 13.08 432 8.46C440 3.85 448 3.85 456 8.46C464 13.08 472 13.08 480 8.46C488 3.85 496 3.85 504 8.46C512 13.08 520 13.08 528 8.46C536 3.85 544 3.85 552 8.46C560 13.08 568 13.08 576 8.46C584 3.85 592 3.85 600 8.46C608 13.08 616 13.08 624 8.46C632 3.85 640 3.85 648 8.46C656 13.08 664 13.08 672 8.46C680 3.85 688 3.85 696 8.46C704 13.08 712 13.08 720 8.46C728 3.85 736 3.85 744 8.46C752 13.08 760 13.08 768 8.46C776 3.85 784 3.85 792 8.46C800 13.08 808 13.08 816 8.46C824 3.85 832 3.85 840 8.46C848 13.08 856 13.08 864 8.46C872 3.85 880 3.85 888 8.46C896 13.08 904 13.08 912 8.46C920 3.85 928 3.85 936 8.46C944 13.08 952 13.08 960 8.46C968 3.85 976 3.85 984 8.46C992 13.08 1000 13.08 1008 8.46C1016 3.85 1024 3.85 1032 8.46C1040 13.08 1048 13.08 1056 8.46C1064 3.85 1072 3.85 1080 8.46C1088 13.08 1096 13.08 1104 8.46C1112 3.85 1120 3.85 1128 8.46C1136 13.08 1144 13.08 1152 8.46C1160 3.85 1168 3.85 1176 8.46C1184 13.08 1192 13.08 1200 8.46V20H0V8.46Z" 
        fill={color}
      />
    </svg>
  </div>
);

// 10-Month Training Curriculum
const TRAINING_JOURNEY = [
  {
    number: 1,
    title: "True Nature",
    phase: "Foundation",
    phaseSubtitle: "Start Here",
    tool: "The True Nature Reference Card",
    shortDesc: "Before you can lead your dog, you have to see them clearly. Most people skip this step.",
    fullDesc: `This card is the foundation everything else is built on. One side gives you the three natures: The Director, The Peacemaker, The Empath. The other side gives you a daily observation checklist. Your only job this month is to use it.

Here is why that matters. Dog psychology is not about tricks. It is about understanding who your dog actually is, how they communicate, what they need, and why they respond the way they do. Before you can lead your dog, you have to see them clearly.

The True Nature Reference Card slows you down on purpose. It asks you to observe before you act. To study before you solve. That is the first lesson in dog psychology, and the first lesson in this curriculum.

Use the checklist daily. Watch your dog at mealtimes, at the door, on the walk, at rest. By the end of this month, you will know exactly who your dog is, and you will start to notice who you are in response to them.`,
    goal: "Identify your dog's True Nature. Everything starts here."
  },
  {
    number: 2,
    title: "Walking in Sync",
    phase: "Foundation",
    phaseSubtitle: "The Leash Is a Language",
    tool: "6-Foot Slip Leash",
    shortDesc: "The leash tells the story of your energy before you ever say a word. This month, you're learning to speak through it.",
    fullDesc: `Now that you know your dog's True Nature, you have context. You know whether you're working with a dog that needs clear direction, patient encouragement, or gentle reassurance.

The slip leash isn't a correction device. It isn't a restraint. In dog psychology, the leash is a communication tool. Every time you tighten your grip, hold your breath, or brace for a pull, your dog feels it.

You will not be walking down a busy street yet. You'll start in your house. Then your yard. Then a quiet, low-distraction zone. This feels slow on purpose. Consistency at low difficulty is how the nervous system learns, yours and your dog's.

This is where the work starts feeling repetitive. Do it anyway. Repetition is not boredom, it's how you build a muscle.`,
    goal: "Learn to communicate through the leash. Master the low-distraction environment before you add complexity."
  },
  {
    number: 3,
    title: "Mealtime Manners",
    phase: "Foundation",
    phaseSubtitle: "Leadership Happens Here, Too",
    tool: "Food Dish",
    shortDesc: "Mealtimes are one of the most powerful leadership moments in your dog's day, and most people give it away completely.",
    fullDesc: `You've been building communication skills for two months. Now you're going to apply them somewhere you never thought to look: the food bowl.

In dog psychology, mealtimes are one of the most powerful leadership moments in your dog's day. The moment your dog rushes the bowl, spins, barks, or pushes past you to eat, leadership transferred.

The food dish in this box isn't just a bowl. It's a daily practice. A Director will test you. A Peacemaker will look to you for permission. An Empath will feel your uncertainty before you notice it yourself.

This box exists before the door and before recall because leadership built in a quiet, low-stakes moment is leadership that holds when the stakes get higher. Do the work at the bowl every day. It takes three minutes. It will change everything.`,
    goal: "Reclaim the food bowl as a daily leadership practice. Watch your dog through the True Nature lens."
  },
  {
    number: 4,
    title: "Calm Before the Door",
    phase: "Application",
    phaseSubtitle: "Your Energy Is the Real Tool",
    tool: "Treats",
    shortDesc: "The threshold is one of the highest-energy moments your dog experiences. No one ever taught them it requires a different energy.",
    fullDesc: `You have built your foundation. You know your dog, you have learned to communicate through the leash, and you have started building structure into your daily routine. This month, you are taking all of that into the most chaotic moment in most dogs' day: the door.

The treats in this box are a communication tool. In dog psychology, food is a marker, a way to say "yes, that." What you are marking is calm. Not sit. Not stay. Calm.

This month, you will desensitize your dog to the sounds and signals of the door long before it opens. You will build the same structure around your car. You will open and close a door a dozen times in a single session without ever stepping outside. It feels pointless. It is not.

You are teaching your dog that the rules do not change based on excitement. Calm is always what gets them what they want.`,
    goal: "Build calm, consistent structure around every threshold, front door and car. Apply it the same way every time."
  },
  {
    number: 5,
    title: "Call to Connection",
    phase: "Application",
    phaseSubtitle: "Does Your Dog Choose You?",
    tool: "15-Foot Slip Leash",
    shortDesc: "When you call, do they choose to come back? Recall is not a command, it's a relationship test.",
    fullDesc: `Four months in, you've been practicing leadership in controlled moments: the walk, the bowl, the door. Now we're going to find out how much of that has transferred into your relationship.

The 15-foot slip leash gives your dog distance, real distance, and asks a real question: when you call, do they choose to come back?

In dog psychology, recall is not a command. It's a relationship test. A dog who doesn't return when called isn't being disobedient, they're telling you that something in the environment is more compelling than you are. That's information. And it's fixable.

This month, you start inside. Then the yard. Then a low-distraction neighborhood block. Every successful recall gets marked. Every repetition deepens the pattern. You're training your dog to choose you.`,
    goal: "Build a recall rooted in real connection, inside the house first, then the yard, then the neighborhood."
  },
  {
    number: 6,
    title: "Play with Purpose",
    phase: "Application",
    phaseSubtitle: "Leadership Doesn't Have to Be Serious",
    tool: "Toy",
    shortDesc: "Play is one of the clearest windows into your relationship. This month gives you permission to play, with intention.",
    fullDesc: `After five months of structure, this box gives you permission to play. The toy in this box is the curriculum. Everything you have built so far shows up in how you and your dog play together.

In dog psychology, play is one of the clearest windows into your relationship. It shows you who initiates, who pushes past the point of fun, who shuts down, who lights up.

Not every dog is a chaser. Not every dog cares about a ball. Your job is to find out what your dog's version of play actually looks like and meet them there. If your dog loves fetch, play fetch with intention. If your dog is a tugger, feel the conversation in your hands.

This is one of the most important months because it teaches you that relationship and connection are built in every moment, including the ones that feel like fun.`,
    goal: "Find your dog's version of play. Lead through it. Have fun doing it. Pay attention to what you both reveal."
  },
  {
    number: 7,
    title: "Mat Magic",
    phase: "Application",
    phaseSubtitle: "One Tool, Infinite Situations",
    tool: "Yoga Mat",
    shortDesc: "The place command is rooted in impulse control. A dog who can hold the mat has learned that calm is a choice.",
    fullDesc: `Six months of work has been building toward this. The mat is where all of it comes together.

The place command is rooted in one of the most important concepts in dog psychology: impulse control. A dog who can go to their mat and hold it, through the doorbell, through dinner, through guests arriving, is a dog who has learned that their energy does not have to run the room.

The yoga mat is portable because the lesson has to travel. You start in a quiet room with zero distraction. You build duration slowly. You practice during dinner before you practice during guests.

This tool will follow you everywhere. Family visits, travel, crowded gatherings, quiet evenings at home. The mat becomes your dog's anchor and your greatest tool in any environment.`,
    goal: "Build the place command in low distraction. Expand slowly. Make the mat the most reliable tool you have."
  },
  {
    number: 8,
    title: "Walking in Sync 2",
    phase: "Transformation",
    phaseSubtitle: "You Are the Tool Now",
    tool: "Fanny Pack with Carabiner",
    shortDesc: "Six months ago, you picked up a slip leash. This month, you put your hands in your pockets. The tool doing the work is you.",
    fullDesc: `Six months ago, you picked up a 6-foot slip leash and started learning to communicate. This month, you put your hands in your pockets. The fanny pack holds your supplies. The carabiner clips the leash to you, so your dog stays connected without your hands on the line. The tool doing the actual work is you.

Going hands-free is a milestone. In dog psychology, it means the leash has done its job. It was always training wheels for communication, for connection, for your confidence as a leader.

This month the walk becomes a regulation practice for you. You breathe intentionally. You walk with purpose. You notice when your body tightens around other dogs, around loud noises, around unexpected situations. Your dog notices too.

Self-regulation is the whole point. In this methodology, everything comes back to the human on the other end of the leash.`,
    goal: "Walk with intentional energy. Regulate yourself first. Let your dog follow."
  },
  {
    number: 9,
    title: "Confidence in Public",
    phase: "Transformation",
    phaseSubtitle: "Take It Into the World",
    tool: "Tote Bag",
    shortDesc: "Eight months of foundation, application, and inner work. This month, you take it somewhere real.",
    fullDesc: `Eight months of foundation, application, and inner work. This month, you take it somewhere real.

Confidence in public is not about your dog being perfect. It's about you being steady. In dog psychology, the handler's energy in a distracting environment determines the dog's response more than any tool, command, or correction ever could.

You'll take your dog somewhere with real distractions, other dogs, strangers, movement, noise. But you won't walk into the middle of it. Distance is a tool. You'll start at the edge of the environment and build in slowly.

Your dog doesn't need to be brave. They need you to be calm. That's the work. This box exists at month nine because it requires all eight months before it.`,
    goal: "Step into the real world with your dog. Use distance as your tool. Trust what you've built."
  },
  {
    number: 10,
    title: "Graduation",
    phase: "Transformation",
    phaseSubtitle: "Full Circle",
    tool: "Journal",
    shortDesc: "The last tool in this curriculum isn't for your dog. It's for you. This is where the transformation becomes real.",
    fullDesc: `The last tool in this curriculum isn't for your dog. It's for you.

The journal is where the transformation becomes real, where you put words to what actually happened over the last ten months. Not just what your dog learned. What you learned. Because that has always been the point.

Dog psychology, at its deepest level, is not about changing the dog. It's about becoming the person your dog has been waiting for.

Your homework this month is to go back to the Box 1 checklist. Look at what you wrote about your dog on day one. Compare it to who you both are now. Then open the journal and answer three questions: How has my dog changed? How have I changed? What is different about the person I am today versus the person who opened Box 1?

Ten months. One tool at a time. This is what doing the work looks like.`,
    goal: "Reflect. Celebrate. Recognize the full transformation, in your dog, and in yourself."
  }
];

const FEATURES = [
  { icon: Package, text: "ONE TOOL DELIVERED EVERY MONTH, WITH THE LESSON BEHIND IT." },
  { icon: CheckCircle, text: "STEP BY STEP, IN THE ORDER THE WORK ACTUALLY HAPPENS." },
  { icon: Heart, text: "BUILT ON RELATIONSHIP, NOT OBEDIENCE." },
  { icon: ThumbsUp, text: "TEN MONTHS OF STRUCTURE YOU CAN FOLLOW WITHOUT GUESSING." },
];

const FAQS = [
  {
    question: "What is inside each Edu Paws box?",
    answer: "Each monthly box includes a tool specific to that month's lesson, access to a step-by-step video, and supporting materials. The tools build on each other, from the True Nature Reference Card in Month 1 to the Journal in Month 10."
  },
  {
    question: "What makes Edu Paws different?",
    answer: "Edu Paws is a 10-month transformation curriculum rooted in dog psychology, not tricks. Each box builds on the last, taking you through three phases: Foundation (understanding your dog), Application (practicing in real situations), and Transformation (becoming the person your dog needs)."
  },
  {
    question: "How do I access the video lessons?",
    answer: "Once you subscribe, you get access to the Edu Paws app where all your lessons are stored. Log in with your account and watch anytime. Each month's video corresponds to that month's tool and theme."
  },
  {
    question: "What is True Nature?",
    answer: "True Nature is the foundation of the Edu Paws framework. There are three natures: The Director (confident, takes charge), The Peacemaker (calm, looks to you for guidance), and The Empath (sensitive, feels your energy). Month 1 teaches you to identify your dog's True Nature."
  },
  {
    question: "Do members get live sessions?",
    answer: "Yes. Members get access to weekly live community sessions. It is a good opportunity to ask questions, get personalized advice, and connect with other families on the same path through the curriculum."
  },
  {
    question: "Can I start if my dog has behavioral issues?",
    answer: "Yes. This curriculum is designed to address the root cause of behavioral issues, which is usually a lack of understanding and structure. The Foundation phase (Months 1-3) helps you see your dog clearly before trying to change anything."
  },
  {
    question: "What if I miss a month or fall behind?",
    answer: "Your boxes will continue to arrive, and you can catch up at your own pace. The curriculum is designed to be followed in order, but taking extra time on a challenging month is better than rushing through without doing the work."
  },
  {
    question: "How do I cancel my subscription?",
    answer: "Head to your account settings in the app and click Cancel Subscription. No hidden fees. You can rejoin anytime you are ready to continue."
  },
];

const COMPARISON = [
  { feature: "Toys for Your Dog", edupaws: true, others: true },
  { feature: "10-Month Structured Curriculum", edupaws: true, others: false },
  { feature: "Weekly Community Lives with Trainer(s)", edupaws: true, others: false },
  { feature: "Tools Curated For Each Phase", edupaws: true, others: false },
  { feature: "Dog Psychology Foundation", edupaws: true, others: false },
];

const PHASES = [
  { name: "Foundation", months: "1-3", color: "bg-edupaws-navy", description: "Understand your dog" },
  { name: "Application", months: "4-7", color: "bg-edupaws-sage", description: "Practice in real situations" },
  { name: "Transformation", months: "8-10", color: "bg-edupaws-gold", description: "Become the leader" },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div 
    className="bg-white rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
    onClick={onClick}
  >
    <div className="flex items-center justify-between p-5 md:p-6">
      <h4 className="font-heading text-lg md:text-xl font-extrabold text-edupaws-navy pr-4">
        {question}
      </h4>
      <ChevronDown className={`w-6 h-6 text-edupaws-navy flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </div>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
      <p className="px-5 md:px-6 pb-5 md:pb-6 text-gray-600 font-body text-base md:text-lg leading-relaxed">
        {answer}
      </p>
    </div>
  </div>
);

const getPhaseColor = (phase) => {
  switch(phase) {
    case 'Foundation': return 'bg-edupaws-navy';
    case 'Application': return 'bg-edupaws-sage';
    case 'Transformation': return 'bg-amber-500';
    default: return 'bg-gray-500';
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [selectedBox, setSelectedBox] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  const menuItems = [
    { label: 'Home', action: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
    { label: 'Dog Psychology Quiz', action: () => navigate('/quiz'), highlight: false },
    { label: 'The Curriculum', action: () => document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' }) },
    { label: 'FAQs', action: () => document.getElementById('faqs')?.scrollIntoView({ behavior: 'smooth' }) },
    { label: 'Login', action: () => navigate('/login') },
    { label: 'Join the Pack', action: () => navigate('/signup'), highlight: true },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl animate-slide-in">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <img 
                src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=130" 
                alt="Edu Paws" 
                className="h-10"
              />
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-gray-500 hover:text-edupaws-navy transition-colors"
                data-testid="close-menu-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Menu Items */}
            <nav className="p-6">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    item.action();
                    setMobileMenuOpen(false);
                  }}
                  data-testid={`menu-item-${index}`}
                  className={`w-full text-left px-5 py-4 rounded-xl font-body text-lg font-semibold transition-all mb-2
                    ${item.highlight 
                      ? 'bg-edupaws-sage text-white' 
                      : 'text-edupaws-navy hover:bg-edupaws-cream'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            
            {/* Menu Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-100 bg-edupaws-cream">
              <div className="text-center">
                <div className="font-heading text-2xl font-bold text-edupaws-navy">$75<span className="text-sm">/mo</span></div>
                <p className="text-xs text-gray-500 font-body mt-1">10-month transformation curriculum</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="py-4 px-6 flex items-center justify-between max-w-7xl mx-auto">
        <button 
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 text-edupaws-navy hover:bg-edupaws-cream rounded-lg transition-colors"
          data-testid="mobile-menu-btn"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        
        <div className="flex items-center gap-2">
          <img 
            src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=130" 
            alt="Edu Paws" 
            className="h-12"
          />
        </div>
        
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/login')}
            className="text-edupaws-navy hover:text-edupaws-dark transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </button>
          <button
            onClick={() => navigate('/signup')}
            data-testid="shop-btn"
            className="bg-edupaws-sage text-white font-semibold py-2 px-5 rounded-full text-sm
                       shadow-[0_3px_0_#7A8F6E] hover:shadow-[0_1px_0_#7A8F6E] hover:translate-y-[2px] transition-all"
          >
            Join the Pack
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-edupaws-navy leading-tight mb-4">
              A 10-Month Transformation for You & Your Dog
            </h1>
            <p className="text-lg md:text-xl text-edupaws-sage font-heading font-bold italic mb-6">
              You came for the dog. You found yourself.
            </p>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 font-body italic leading-relaxed">
              <strong className="not-italic">Edu Paws</strong> is a dog psychology curriculum, not tricks, not bribes, not quick fixes. 
              It is about understanding who your dog actually is and becoming the person they need.
            </p>
            
            {/* Price Badge */}
            <div className="inline-flex items-center gap-6 bg-edupaws-cream rounded-2xl px-8 py-5 mb-10">
              <div>
                <div className="font-heading text-4xl font-bold text-edupaws-navy">$75<span className="text-xl">/mo</span></div>
                <div className="text-base text-gray-500 font-body">10-month curriculum</div>
              </div>
              <div className="h-16 w-px bg-gray-300"></div>
              <div className="text-base text-gray-600 font-body space-y-1">
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-edupaws-sage" /> Monthly Box + Tool</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-edupaws-sage" /> Video Lessons</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-edupaws-sage" /> Live Sessions</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={() => navigate('/signup')}
                data-testid="join-pack-btn"
                className="bg-edupaws-navy text-white font-bold py-4 px-10 rounded-full text-lg
                           shadow-[0_4px_0_#2D3B47] hover:shadow-[0_2px_0_#2D3B47] hover:translate-y-[2px] transition-all font-body"
              >
                Join the Pack
              </button>
              <button
                onClick={() => navigate('/quiz')}
                data-testid="quiz-cta-btn"
                className="bg-white text-edupaws-navy font-bold py-4 px-10 rounded-full text-lg border-2 border-edupaws-navy
                           hover:bg-edupaws-cream transition-all font-body"
              >
                Take the Dog Psychology Quiz
              </button>
            </div>
          </div>
          <div className="order-1 md:order-2 flex justify-center items-stretch">
            <img 
              src="https://edupaws.co/cdn/shop/files/EduPaws_Dog.png?v=1759709026&width=600" 
              alt="Edu Paws Dog Mascot" 
              className="w-full max-w-lg xl:max-w-xl object-contain"
            />
          </div>
        </div>
      </section>

      {/* Feature Text */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-edupaws-navy">
            Dog Psychology, Not Dog Tricks
          </h2>
          <p className="mt-6 text-xl md:text-2xl text-gray-600 font-body max-w-3xl mx-auto leading-relaxed">
            Most programs teach commands. We teach understanding. Because before you can lead your dog, 
            you have to see them clearly.
          </p>
        </div>
      </section>

      {/* Wave Divider */}
      <WaveDivider color="#9CAF88" />

      {/* Features Section - Sage Background */}
      <section className="bg-edupaws-sage py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
            {FEATURES.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-24 h-24 rounded-full border-2 border-white flex items-center justify-center mx-auto mb-5">
                  <feature.icon className="w-10 h-10 text-white" strokeWidth={1.5} />
                </div>
                <p className="text-white text-sm font-bold uppercase tracking-wide font-body leading-relaxed">
                  {feature.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave Divider Bottom */}
      <WaveDivider color="#9CAF88" flip={true} />

      {/* The Three Phases */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-edupaws-navy mb-5">
              The Three Phases
            </h2>
            <p className="text-xl text-gray-600 font-body">
              Each phase builds on the last. You can't skip ahead, and you won't want to.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PHASES.map((phase, index) => (
              <div key={index} className="text-center h-full">
                <div className={`${phase.color} text-white rounded-2xl p-8 h-full flex flex-col justify-center`}>
                  <div className="text-base font-body uppercase tracking-wide opacity-80">Months {phase.months}</div>
                  <h3 className="font-heading text-3xl font-extrabold mt-2">{phase.name}</h3>
                  <p className="font-body mt-3 text-lg opacity-90">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Journey Section - 10 Boxes */}
      <section id="curriculum" className="py-24 px-6 bg-edupaws-cream">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-edupaws-navy mb-5">
              Your 10-Month Journey
            </h2>
            <p className="text-xl text-gray-600 font-body max-w-2xl mx-auto">
              One tool per month. One lesson per month. Ten months of transformation, 
              all included in your <strong>$75/month</strong> subscription.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {TRAINING_JOURNEY.map((box, index) => (
              <div 
                key={index}
                onClick={() => setSelectedBox(box)}
                data-testid={`box-card-${index + 1}`}
                className="bg-white rounded-2xl border border-gray-100 p-6 cursor-pointer
                           shadow-[0_4px_15px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)]
                           hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full bg-edupaws-sage/20 flex items-center justify-center">
                    <span className="text-edupaws-sage font-bold font-body text-base">{box.number}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold font-body text-white ${getPhaseColor(box.phase)}`}>
                    {box.phase}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-extrabold text-edupaws-navy mb-2">
                  {box.title}
                </h3>
                <p className="text-gray-400 text-sm font-body mb-2">{box.tool}</p>
                <p className="text-gray-500 text-sm font-body line-clamp-2">{box.shortDesc}</p>
                <div className="flex items-center text-edupaws-sage text-sm font-bold font-body mt-4">
                  View Details <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-edupaws-navy">
              Edu Paws Compared To The Rest
            </h2>
          </div>

          <WaveDivider color="#9CAF88" />

          <div className="bg-white border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-3">
              <div className="p-5 border-b border-r border-gray-200"></div>
              <div className="p-5 bg-edupaws-navy text-white text-center border-b">
                <span className="font-heading text-xl font-extrabold">Edu Paws</span>
              </div>
              <div className="p-5 text-center border-b border-gray-200">
                <span className="font-heading text-xl font-extrabold text-edupaws-navy">Other Boxes</span>
              </div>
            </div>

            {COMPARISON.map((row, index) => (
              <div key={index} className="grid grid-cols-3">
                <div className="p-5 border-r border-b border-gray-200 flex items-center">
                  <span className="font-body text-gray-700 text-base">{row.feature}</span>
                </div>
                <div className="p-5 bg-edupaws-navy/5 border-b flex items-center justify-center">
                  <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                    row.edupaws ? 'border-edupaws-navy text-edupaws-navy' : 'border-gray-300 text-gray-300'
                  }`}>
                    {row.edupaws ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
                  </div>
                </div>
                <div className="p-5 border-b border-gray-200 flex items-center justify-center">
                  <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                    row.others ? 'border-gray-400 text-gray-400' : 'border-gray-300 text-gray-300'
                  }`}>
                    {row.others ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <WaveDivider color="#9CAF88" />
      <section id="faqs" className="bg-edupaws-sage py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-2 mb-5">
              <PawPrint className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-4">
              Join the Edu Paws Pack
            </h2>
            <p className="text-xl text-white/80 font-body">
              Everything you need to know before starting your transformation
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {FAQS.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaq === index}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>
      <WaveDivider color="#9CAF88" flip={true} />

      {/* Subscription CTA */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-edupaws-navy mb-5">
            Ready to Begin?
          </h2>
          <p className="text-xl text-gray-600 font-body mb-12 max-w-2xl mx-auto">
            Ten months. One tool at a time. A complete transformation, for your dog and for you.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Monthly */}
            <div className="bg-edupaws-cream rounded-3xl p-8 shadow-md flex flex-col">
              <div className="text-edupaws-sage font-body font-bold text-sm uppercase tracking-wide mb-2">
                Monthly
              </div>
              <div className="font-heading text-5xl font-bold text-edupaws-navy mb-1">
                $75<span className="text-2xl">/mo</span>
              </div>
              <p className="text-gray-500 font-body text-sm mb-6">10-month curriculum</p>
              <div className="space-y-3 text-left mb-8 flex-1">
                {["One box per month", "Video lesson each month", "Weekly live sessions", "Community access"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-700 font-body text-sm">
                    <CheckCircle className="w-5 h-5 text-edupaws-sage flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate(promoCode.trim() ? `/signup?promo=${encodeURIComponent(promoCode.trim())}` : '/signup')}
                data-testid="subscribe-cta-monthly"
                className="w-full bg-edupaws-navy text-white font-bold py-4 px-6 rounded-full text-base
                           shadow-[0_3px_0_#2D3B47] hover:shadow-[0_1px_0_#2D3B47] hover:translate-y-[2px] transition-all font-body"
              >
                Start Your Transformation
              </button>
            </div>

            {/* Full Package - Best Value */}
            <div className="bg-edupaws-cream rounded-3xl p-8 shadow-lg flex flex-col ring-2 ring-edupaws-sage relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-edupaws-sage text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                Best Value
              </div>
              <div className="text-edupaws-sage font-body font-bold text-sm uppercase tracking-wide mb-2">
                Full Package
              </div>
              <div className="font-heading text-5xl font-bold text-edupaws-navy mb-1">
                $675
              </div>
              <p className="text-gray-500 font-body text-sm mb-6">One-time payment, save 10%</p>
              <div className="space-y-3 text-left mb-8 flex-1">
                {["All 10 boxes included", "Video lesson each month", "Weekly live sessions", "Community access", "Save $75 vs monthly"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-700 font-body text-sm">
                    <CheckCircle className="w-5 h-5 text-edupaws-sage flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate(promoCode.trim() ? `/signup?promo=${encodeURIComponent(promoCode.trim())}` : '/signup')}
                data-testid="subscribe-cta-fullpackage"
                className="w-full bg-edupaws-sage text-white font-bold py-4 px-6 rounded-full text-base
                           shadow-[0_3px_0_#7A8F6E] hover:shadow-[0_1px_0_#7A8F6E] hover:translate-y-[2px] transition-all font-body"
              >
                Start Your Transformation
              </button>
            </div>

            {/* Accelerated */}
            <div className="bg-edupaws-cream rounded-3xl p-8 shadow-md flex flex-col">
              <div className="text-edupaws-sage font-body font-bold text-sm uppercase tracking-wide mb-2">
                Accelerated
              </div>
              <div className="font-heading text-5xl font-bold text-edupaws-navy mb-1">
                $825
              </div>
              <p className="text-gray-500 font-body text-sm mb-6">One-time, ships every 2 weeks</p>
              <div className="space-y-3 text-left mb-8 flex-1">
                {["All 10 boxes included", "New box every 2 weeks", "Video lesson each month", "Weekly live sessions", "Community access"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-700 font-body text-sm">
                    <CheckCircle className="w-5 h-5 text-edupaws-sage flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate(promoCode.trim() ? `/signup?promo=${encodeURIComponent(promoCode.trim())}` : '/signup')}
                data-testid="subscribe-cta-accelerated"
                className="w-full bg-edupaws-navy text-white font-bold py-4 px-6 rounded-full text-base
                           shadow-[0_3px_0_#2D3B47] hover:shadow-[0_1px_0_#2D3B47] hover:translate-y-[2px] transition-all font-body"
              >
                Start Your Transformation
              </button>
            </div>
          </div>

          {/* Promo Code Input */}
          <div className="max-w-md mx-auto mb-8">
            <div className="flex items-center gap-2 justify-center mb-3">
              <Tag className="w-4 h-4 text-edupaws-sage" />
              <span className="text-sm font-body font-medium text-gray-500">Have a promo code?</span>
            </div>
            <div className="flex gap-2 max-w-xs mx-auto">
              <input
                type="text"
                data-testid="landing-promo-input"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="flex-1 h-11 px-4 rounded-full border border-gray-200 bg-white text-sm font-body text-center uppercase tracking-wider focus:outline-none focus:border-edupaws-sage focus:ring-2 focus:ring-edupaws-sage/20"
              />
              <button
                data-testid="landing-promo-apply-btn"
                onClick={() => {
                  if (promoCode.trim()) {
                    navigate(`/signup?promo=${encodeURIComponent(promoCode.trim())}`);
                  }
                }}
                disabled={!promoCode.trim()}
                className="px-5 h-11 rounded-full bg-edupaws-navy text-white text-sm font-body font-bold hover:bg-edupaws-navy/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
          
          <p className="text-gray-400 font-body text-base">
            Free shipping on all boxes
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-edupaws-navy text-white">
        <div className="max-w-7xl mx-auto text-center">
          <img 
            src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" 
            alt="Edu Paws" 
            className="h-12 mx-auto mb-5 brightness-0 invert"
          />
          <p className="text-white/60 text-base font-body">
            © 2026 Edu Paws. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Box Detail Modal */}
      <Dialog open={!!selectedBox} onOpenChange={() => setSelectedBox(null)}>
        <DialogContent className="sm:max-w-2xl bg-white border-0 shadow-xl max-h-[90vh] overflow-y-auto">
          {selectedBox && (
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-edupaws-sage/20 flex items-center justify-center">
                    <span className="text-edupaws-sage font-bold text-xl font-body">{selectedBox.number}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold font-body text-white ${getPhaseColor(selectedBox.phase)}`}>
                        {selectedBox.phase}
                      </span>
                      <span className="text-gray-400 text-xs font-body">{selectedBox.phaseSubtitle}</span>
                    </div>
                    <h3 className="font-heading text-2xl font-extrabold text-edupaws-navy">
                      Box {selectedBox.number}: {selectedBox.title}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="bg-edupaws-cream rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 text-edupaws-navy">
                  <Package className="w-5 h-5" />
                  <span className="font-body font-semibold">Your Tool:</span>
                  <span className="font-body">{selectedBox.tool}</span>
                </div>
              </div>
              
              <div className="prose prose-sm max-w-none mb-6">
                {selectedBox.fullDesc.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-gray-600 font-body text-sm mb-3">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="bg-edupaws-navy rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-body font-semibold text-white text-sm mb-1">Your Goal This Month:</div>
                    <p className="text-white/90 font-body text-sm">{selectedBox.goal}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => { setSelectedBox(null); navigate('/signup'); }}
                data-testid="get-box-modal-btn"
                className="w-full bg-edupaws-sage text-white font-semibold py-3 px-6 rounded-full
                           shadow-[0_4px_0_#7A8F6E] hover:shadow-[0_2px_0_#7A8F6E] hover:translate-y-[2px] transition-all font-body"
              >
                Start Your Transformation
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
